import api from "@/app/api";
import Guid from "@/app/types/common/guid";
import { Instant, LocalDate, LocalTime } from "@js-joda/core";
import {ModelObject, ModelObjectWithDirtyCheck, type ValidationRule} from "@/app/services/modelObject";
import ServiceBase from "@/app/services/serviceBase";
import converters from "@/app/services/converters"

// Model Objects

export class PricingCategory extends ModelObjectWithDirtyCheck<PricingCategory> {

    public version: number = 0;
    public id: Guid = Guid.createEmpty();
    /**
    * Pricing Category
    * Not Nullable
    * Not Empty
    * Max Length : 1000
    */
    public name: string = "";
    public isHidden: boolean = false;

    public constructor(jsonSerializedObject?: any) {
        super();
        if (jsonSerializedObject) {
          this.version = jsonSerializedObject.version;
          this.id = Guid.parseOrEmpty(jsonSerializedObject.id);
          this.name = jsonSerializedObject.name;
          this.isHidden = jsonSerializedObject.isHidden;
        }

        this.setDirtyCheckpoint();
    }

    public toJsonSerializedObject(): any {
        const toRet = {} as any;
        toRet.version = this.version;
        toRet.id = this.id.toString();
        toRet.name = this.name;
        toRet.isHidden = this.isHidden;
        return toRet;
    }

    public toProto(): any {
      return this.toJsonSerializedObject();
    }

    public toBinary(): Uint8Array {
      return new TextEncoder().encode(JSON.stringify(this.toJsonSerializedObject()));
    }

    public validationRules: ValidationRule = [
            {field: "version", rule: (v: any) => v == null || v >= -9223372036854775808 || "Version is not greater than minimum allowed value: -9223372036854775808"},
            {field: "version", rule: (v: any) => v == null || v <= 9223372036854775807 || "Version is above maximum allowed value: 9223372036854775807"},
            {field: "id", rule: (v: any) => !v.isEmpty() || "PricingCategory.Id cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length !== 0 || "Name cannot be empty"},
            {field: "name", rule: (v: any) => v == null || v.length <= 1000 || "Name has too many characters (1000)"},
    ];
}


export class PricingCategoryService extends ServiceBase {

  public async getAllPricingCategories(opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(PricingCategory)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    };
    const extractor = (response:any) => response.pricingCategories.map((v: any) => new PricingCategory(v));;
    return this.makeJsonRequest("pricingCategory/v1/getAllPricingCategories", req, false, extractor, opts);
  };

  public async getPricingCategory(pricingCategoryId: Guid, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<PricingCategory> {
    const req = {
    tenantId:api.tenantId.toString(),
    pricingCategoryId:pricingCategoryId.toString(),
    };
    const extractor = (response:any) => new PricingCategory(response.pricingCategory);;
    return this.makeJsonRequest("pricingCategory/v1/getPricingCategory", req, false, extractor, opts);
  };

  public async savePricingCategory(pricingCategory: PricingCategory, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    pricingCategory:pricingCategory.toJsonSerializedObject(),
    };
    const extractor = null;
    return this.makeJsonRequest("pricingCategory/v1/savePricingCategory", req, true, extractor, opts);
  };

  public async setPricingCategoryIsHidden(pricingCategoryId: Guid, hide: boolean, opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<void> {
    const req = {
    tenantId:api.tenantId.toString(),
    pricingCategoryId:pricingCategoryId.toString(),
    hide:hide,
    };
    const extractor = null;
    return this.makeJsonRequest("pricingCategory/v1/setPricingCategoryIsHidden", req, true, extractor, opts);
  };

  public async getPricingCategories(pricingCategoryIds: readonly Guid[], opts?: {requestTimeoutMs?: number, sessionToken?: string}): Promise<(PricingCategory)[]> {
    const req = {
    tenantId:api.tenantId.toString(),
    pricingCategoryIds:pricingCategoryIds.map(v => v.toString()),
    };
    const extractor = (response:any) => response.pricingCategory.map((v: any) => new PricingCategory(v));;
    return this.makeJsonRequest("pricingCategory/v1/getPricingCategories", req, false, extractor, opts);
  };
}

export const pricingCategoryService = new PricingCategoryService();